/**
 * /erp/manufacturing/new — create a planned manufacturing job (Manager+).
 *
 * Three required fields: product, qty, warehouse. The product picker
 * shows active products; the warehouse picker shows active warehouses.
 * Manufacturing does not deduct raw materials — that's done manually.
 *
 * Submitting redirects to the detail page so the manager can hit
 * "Start" right away.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, Factory, Loader2, Plus, X } from "lucide-react";
import { type FormEvent, useState } from "react";

import {
  ApiError,
  type ErpMfgJobCreate,
  erpApi,
  isErpCatalogConflict,
  isErpMfgConflict,
} from "../lib/api";
import { useProducts, useWarehouses } from "../lib/erp";
import { toast } from "../lib/toast";
import { sanitizeQtyInput } from "../lib/units";

export const Route = createFileRoute("/_app/erp/manufacturing/new")({
  component: NewManufacturingJobPage,
});

function NewManufacturingJobPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const productsQ = useProducts();
  const warehousesQ = useWarehouses();

  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [code, setCode] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const products = (productsQ.data?.items ?? []).filter((p) => p.is_active);
  const warehouses = (warehousesQ.data?.items ?? []).filter((w) => w.is_active);
  const selectedProduct = products.find((p) => p.id === productId);

  const mutation = useMutation({
    mutationFn: (body: ErpMfgJobCreate) => erpApi.manufacturing.create(body),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "manufacturing-jobs"] });
      toast.success(`Job ${job.code} created.`);
      navigate({ to: "/erp/manufacturing/$jobId", params: { jobId: job.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const detail = err.detail;
        if (isErpMfgConflict(detail) || isErpCatalogConflict(detail)) {
          setError(detail.message);
          return;
        }
      }
      const msg = err instanceof ApiError ? err.message : "Could not create job.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!productId) {
      setError("Pick a product.");
      return;
    }
    if (!qty.trim() || Number(qty) <= 0) {
      setError("Qty must be a positive number.");
      return;
    }
    if (!warehouseId) {
      setError("Pick a warehouse.");
      return;
    }
    const body: ErpMfgJobCreate = {
      product_id: productId,
      qty: qty.trim(),
      warehouse_id: warehouseId,
    };
    if (code.trim()) body.code = code.trim();
    if (notes.trim()) body.notes = notes.trim();
    mutation.mutate(body);
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-4 sm:p-6">
      <header className="space-y-1">
        <Link
          to="/erp/manufacturing"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
        >
          <ArrowLeft className="size-3" aria-hidden /> Manufacturing jobs
        </Link>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Factory className="size-5 opacity-70" aria-hidden />
          New manufacturing job
        </h1>
        <p className="text-sm opacity-60">
          Plan a production run. The job stays in "planned" until you start it; raws are deducted at
          start time using the product's active BOM.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-4 rounded-xl border border-[var(--border)] p-4">
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Product <span className="text-red-500">*</span>
          </span>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          >
            <option value="">Select a product…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name} ({p.uom})
              </option>
            ))}
          </select>
          {selectedProduct && (
            <span className="block text-[11px] opacity-60">
              UoM: <span className="font-mono">{selectedProduct.uom}</span>. The job will produce
              this many units of {selectedProduct.name}.
            </span>
          )}
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Qty <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={qty}
            onChange={(e) => setQty(sanitizeQtyInput(e.target.value, selectedProduct?.uom))}
            placeholder="e.g. 100"
            inputMode="decimal"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Warehouse <span className="text-red-500">*</span>
          </span>
          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          >
            <option value="">Select a warehouse…</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
          <span className="block text-[11px] opacity-60">
            Raws are deducted from this warehouse at start; finished products are credited here at
            completion.
          </span>
        </label>

        <details className="space-y-2 rounded-md border border-[var(--border)] p-3 text-sm">
          <summary className="cursor-pointer opacity-80">Optional fields</summary>
          <label className="block space-y-1 pt-2">
            <span className="text-xs font-medium opacity-80">Job code</span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Auto-generated if blank (MFG-…)"
              maxLength={32}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Free-form notes for the floor team."
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            />
          </label>
        </details>

        {error && (
          <p className="flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
            <AlertCircle className="size-3" aria-hidden />
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Link
            to="/erp/manufacturing"
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden />
            Cancel
          </Link>
          <button
            type="submit"
            disabled={mutation.isPending || !productId || !qty.trim() || !warehouseId}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            Create job
          </button>
        </div>
      </form>
    </div>
  );
}
